/**
 * WelderContinuityLog - Full-featured welder continuity tracking
 * 
 * Accessed from Quality department menu.
 * Features:
 * - Global view (not project-scoped) with job number filter
 * - Searchable job number/name dropdown
 * - Process dropdown from db_welding_procedure
 * - Certificate date & expiry date
 * - Last NDE operated date (auto-computed from NDE tracker)
 * - 6-month expiry flagging
 * - Need Certificate analytics tab
 * - Excel import/export
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import { FilterIcon, FilterTemplateDropdown, useFilterTemplate } from '../common/FilteringTemplate';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import EmployeeAutocomplete from '../common/EmployeeAutocomplete';
import { EmployeeHelpShell } from '../common/EmployeeCodeLegendPanel';
import { ListenButton } from '../common/AudioGuide';

let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}

const WelderContinuityLog = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('welderContinuity', 'global', true);
  const [loading, setLoading] = useState(true);
  const [welders, setWelders] = useState([]);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWelder, setNewWelder] = useState({
    empl_code: '', empl_name: '', welder_number: '', shop_field: 'field',
    process: '', certificate_date: '', expiry_date: '', job_number_ref: '', job_name: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [importing, setImporting] = useState(false);
  const [activeView, setActiveView] = useState('log'); // 'log' or 'analytics'
  const [helpOpen, setHelpOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const fetchWelders = useCallback(async () => {
    try {
      setLoading(true);
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/welder-log/all${params}`);
      if (response.data.success) {
        setWelders(response.data.data.rows || []);
      }
    } catch (err) {
      setError('Failed to fetch welders');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, search]);

  const fetchProcedures = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welding-procedures');
      if (response.data.success) {
        setProcedures(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch procedures:', err);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchWelders(); }, [fetchWelders]);
  useEffect(() => { fetchProcedures(); }, [fetchProcedures]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welder-log/analytics/need-certificate');
      if (response.data.success) {
        setAnalyticsData(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (activeView === 'analytics') {
      fetchAnalytics();
    }
  }, [activeView, fetchAnalytics]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleSave = async (welder) => {
    if (!welder.empl_code?.trim() || !welder.empl_name?.trim()) {
      setError('Select an employee from the searchable dropdown (code + name required)');
      return;
    }
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welder-log/save', { welder });
      if (response.data.success) {
        setEditingId(null);
        setShowAddForm(false);
        setNewWelder({ empl_code: '', empl_name: '', welder_number: '', shop_field: 'field', process: '', certificate_date: '', expiry_date: '', job_number_ref: '', job_name: '' });
        setSuccessMsg('Welder saved successfully');
        fetchWelders();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save welder');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/welder-log/${id}`);
      setSuccessMsg('Entry deleted');
      fetchWelders();
    } catch (err) {
      setError('Failed to delete entry');
    }
  };

  const handleExport = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welder-log/export', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename({ job: 'portal', reportSlug: 'Welder_Continuity_Log', ext: 'xlsx' });
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welder-log/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) {
        setSuccessMsg(`Imported ${response.data.data.importedCount} entries (${response.data.data.skippedCount} skipped)`);
        fetchWelders();
      }
    } catch (err) {
      setError('Failed to import file');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/welder-log/template', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Welder_Continuity_Log_Template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const { jsPDF, autoTable } = await loadPdfLibs();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const pageW = doc.internal.pageSize.getWidth();
      const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, omitJobLine: true, margin: 14 });

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('Welder Continuity Log', 14, letterheadY);

      const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${now} | Entries: ${sortedData.length}`, pageW - 14, 10, { align: 'right' });

      const pdfColumns = [
        { header: 'Welder #', dataKey: 'welder_number' },
        { header: 'Empl Name', dataKey: 'empl_name' },
        { header: 'Process', dataKey: 'process' },
        { header: 'Shop/Field', dataKey: 'shop_field' },
        { header: 'Cert. Date', dataKey: 'certificate_date' },
        { header: 'Expiry Date', dataKey: 'expiry_date' },
        { header: 'Last NDE Operated', dataKey: 'last_nde_operated_date' },
        { header: 'Last NDE Job', dataKey: 'last_nde_job' },
        { header: 'Job', dataKey: 'job_ref' },
      ];

      const pdfRows = sortedData.map(w => ({
        welder_number: w.welder_number || '-',
        empl_name: w.empl_name || '-',
        process: w.process || '-',
        shop_field: w.shop_field || '-',
        certificate_date: w.certificate_date ? new Date(w.certificate_date).toLocaleDateString() : '-',
        expiry_date: w.expiry_date ? new Date(w.expiry_date).toLocaleDateString() : '-',
        last_nde_operated_date: w.last_nde_operated_date ? new Date(w.last_nde_operated_date).toLocaleDateString() : '-',
        last_nde_job: w.last_nde_job_number ? `${w.last_nde_job_number}${w.last_nde_job_name ? ' - ' + w.last_nde_job_name : ''}` : '-',
        job_ref: w.job_number_ref ? `${w.job_number_ref}${w.job_name ? ' - ' + w.job_name : ''}` : '-',
      }));

      autoTable(doc, {
        columns: pdfColumns,
        body: pdfRows,
        startY: letterheadY + 6,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const row = sortedData[data.row.index];
            if (row && row.needs_renewal) {
              data.cell.styles.fillColor = [240, 230, 230];
              data.cell.styles.textColor = [180, 30, 30];
            } else if (row && row.is_warning) {
              data.cell.styles.fillColor = [254, 249, 230];
              data.cell.styles.textColor = [180, 100, 0];
            }
          }
        },
      });

      doc.save(exportFilename({ job: 'portal', reportSlug: 'Welder_Continuity_Log', ext: 'pdf' }));
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to generate PDF');
    }
  };

  const inputStyle = {
    padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
    fontSize: '13px', width: '100%',
  };

  const btnStyle = (bg, color = 'white') => ({
    padding: '5px 12px', backgroundColor: bg, color, border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
  });

  const columns = [
    { key: 'empl_code', label: 'Empl Code', width: '90px', type: 'text' },
    { key: 'empl_name', label: 'Empl Name', width: '140px', type: 'text' },
    { key: 'welder_number', label: 'Welder #', width: '100px', type: 'text' },
    { key: 'shop_field', label: 'Shop/Field', width: '80px', type: 'text' },
    { key: 'process', label: 'Process', width: '100px', type: 'text' },
    { key: 'certificate_date', label: 'Cert. Date', width: '110px', type: 'date' },
    { key: 'expiry_date', label: 'Expiry Date', width: '110px', type: 'date' },
    { key: 'last_nde_operated_date', label: 'Last NDE Operated', width: '130px', type: 'date' },
    { key: 'last_nde_job_number', label: 'Last NDE Job #', width: '110px', type: 'text' },
    { key: 'last_nde_job_name', label: 'Last NDE Job Name', width: '150px', type: 'text' },
    { key: 'job_number_ref', label: 'Job Number', width: '110px', type: 'text' },
    { key: 'job_name', label: 'Job Name', width: '160px', type: 'text' },
    { key: 'updated_by', label: 'Updated By', width: '150px', type: 'text' },
    { key: 'updated_at', label: 'Updated At', width: '140px', type: 'date' },
  ];

  // Filter hook
  const {
    filteredItems,
    columnFilters,
    openFilterColumn,
    filterSearch,
    filterMode,
    filterText,
    filterButtonPositions,
    setOpenFilterColumn,
    setFilterSearch,
    getUniqueValues,
    handleFilterToggle,
    clearColumnFilter,
    selectAllColumnFilter,
    applyTextFilter,
    clearAllFilters,
    getActiveFilters,
    openFilterAtPosition,
    numericColumnKeys,
    dateColumnKeys,
  } = useFilterTemplate({ items: welders, columns });

  // Sort logic
  const handleSort = (columnKey) => {
    setSortConfig(prev => {
      if (prev.key !== columnKey) return { key: columnKey, direction: 'asc' };
      if (prev.direction === 'asc') return { key: columnKey, direction: 'desc' };
      return { key: null, direction: null };
    });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredItems;
    const col = columns.find(c => c.key === sortConfig.key);
    return [...filteredItems].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (col?.type === 'date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sortConfig, columns]);

  const getSortIcon = (colKey) => {
    if (sortConfig.key !== colKey) return '⇅';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  const activeFilters = getActiveFilters();

  const renderEditableCell = (form, setForm, field) => {
    if (field === 'last_nde_operated_date' || field === 'last_nde_job_number' || field === 'last_nde_job_name') {
      return <span style={{ fontSize: '12px', color: '#6b7280' }}>Auto</span>;
    }
    if (field === 'empl_code' || field === 'empl_name') {
      // Single searchable dropdown spanning both columns — only render on empl_code
      if (field === 'empl_name') {
        return <span style={{ fontSize: 12, color: '#6b7280' }}>{form.empl_name || '—'}</span>;
      }
      return (
        <EmployeeAutocomplete
          value={form.empl_code ? { emp_code: form.empl_code, emp_name: form.empl_name } : null}
          onChange={(emp) => {
            if (!emp) {
              setForm((p) => ({ ...p, empl_code: '', empl_name: '' }));
              return;
            }
            setForm((p) => ({
              ...p,
              empl_code: emp.emp_code || emp.empl_code || '',
              empl_name: emp.emp_name || emp.empl_name || '',
            }));
          }}
          placeholder="Search active employees…"
          style={{ width: '100%', minWidth: 180 }}
        />
      );
    }
    if (field === 'shop_field') {
      return (
        <select value={form.shop_field || 'field'} onChange={(e) => setForm(p => ({ ...p, shop_field: e.target.value }))} style={inputStyle}>
          <option value="field">Field</option>
          <option value="shop">Shop</option>
        </select>
      );
    }
    if (field === 'process') {
      return (
        <select value={form.process || ''} onChange={(e) => setForm(p => ({ ...p, process: e.target.value }))} style={inputStyle}>
          <option value="">--</option>
          {procedures.map(p => <option key={p.id} value={p.process}>{p.process}</option>)}
        </select>
      );
    }
    if (field === 'certificate_date' || field === 'expiry_date') {
      return (
        <input type="date" value={form[field] ? form[field].substring(0, 10) : ''} onChange={(e) => setForm(p => ({ ...p, [field]: e.target.value }))} style={inputStyle} />
      );
    }
    if (field === 'job_number_ref' || field === 'job_name') {
      return <JobSearchInput form={form} setForm={setForm} field={field} inputStyle={inputStyle} instance={instance} accounts={accounts} />;
    }
    return (
      <input value={form[field] || ''} onChange={(e) => setForm(p => ({ ...p, [field]: e.target.value }))} style={inputStyle} />
    );
  };

  const formatCellValue = (row, field) => {
    if (field === 'updated_at') {
      return row[field] ? new Date(row[field]).toLocaleString() : '-';
    }
    if (field === 'certificate_date' || field === 'expiry_date' || field === 'last_nde_operated_date') {
      return row[field] ? new Date(row[field]).toLocaleDateString() : '-';
    }
    if (field === 'shop_field') {
      const val = row[field] || 'field';
      return (
        <span style={{
          padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
          backgroundColor: val === 'shop' ? '#fef3c7' : '#dbeafe',
          color: val === 'shop' ? '#92400e' : '#1e40af',
        }}>{val}</span>
      );
    }
    return row[field] || '-';
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: '0', fontSize: '20px', fontWeight: '600', color: '#1e3a5f' }}>
          Welder Continuity Log
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => setHelpOpen(true)}
          style={{ padding: '6px 14px', backgroundColor: '#fff', color: '#1e3a5f', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
        >
          ❓ Help
        </button>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '3px' }}>
          <button
            onClick={() => setActiveView('log')}
            style={{
              padding: '6px 16px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              backgroundColor: activeView === 'log' ? '#fff' : 'transparent',
              color: activeView === 'log' ? '#1e3a5f' : '#6b7280',
              boxShadow: activeView === 'log' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            Continuity Log
          </button>
          <button
            onClick={() => setActiveView('analytics')}
            style={{
              padding: '6px 16px', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              backgroundColor: activeView === 'analytics' ? '#fff' : 'transparent',
              color: activeView === 'analytics' ? '#1e3a5f' : '#6b7280',
              boxShadow: activeView === 'analytics' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            Need Certificate
          </button>
        </div>
        </div>
      </div>

      {activeView === 'analytics' ? (
        <NeedCertificateAnalytics data={analyticsData} loading={analyticsLoading} />
      ) : (
        <>
          {/* Toolbar */}
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search welders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ padding: '6px 12px', paddingLeft: '30px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px', width: '250px' }}
                />
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span>
              </div>
              <span style={{ fontSize: '14px', color: '#666' }}>
                {sortedData.length}{sortedData.length !== welders.length ? ` / ${welders.length}` : ''} entries
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => setShowAddForm(!showAddForm)} style={{ padding: '8px 16px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                {showAddForm ? '- Cancel' : '+ Add Entry'}
              </button>
              <button onClick={handleExport} style={{ padding: '8px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                Export
              </button>
              <label style={{ padding: '8px 16px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                {importing ? 'Importing...' : 'Import'}
                <input type="file" accept=".xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} disabled={importing} />
              </label>
              <button onClick={handleDownloadTemplate} style={{ padding: '8px 16px', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                Template
              </button>
              <button onClick={handleDownloadPdf} style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                PDF
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div style={{ padding: '8px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
              {error}
              <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
            </div>
          )}
          {successMsg && (
            <div style={{ padding: '8px 16px', backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
              {successMsg}
            </div>
          )}

          {/* Add Form */}
          {showAddForm && (
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>Add New Entry</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', alignItems: 'end' }}>
                {columns.filter(c => !['updated_by', 'updated_at', 'last_nde_operated_date', 'last_nde_job_number', 'last_nde_job_name'].includes(c.key)).map(col => (
                  <div key={col.key}>
                    <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>{col.label}</label>
                    {renderEditableCell(newWelder, setNewWelder, col.key)}
                  </div>
                ))}
                <button onClick={() => handleSave(newWelder)} disabled={!newWelder.welder_number || !newWelder.empl_code || saving} style={btnStyle(!newWelder.welder_number || !newWelder.empl_code || saving ? '#93c5fd' : '#2563eb')}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {/* Active Filters / Sort indicator */}
          {(activeFilters.length > 0 || sortConfig.key) && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {sortConfig.key && (
                <span style={{ padding: '4px 10px', backgroundColor: '#cce5ff', borderRadius: '4px', fontSize: '12px', color: '#004085' }}>
                  Sorted: {columns.find(c => c.key === sortConfig.key)?.label} ({sortConfig.direction === 'asc' ? 'A→Z' : 'Z→A'})
                </span>
              )}
              {activeFilters.map(f => (
                <span key={f.key} style={{ padding: '4px 10px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '12px', color: '#065f46' }}>
                  {columns.find(c => c.key === f.key)?.label}: {f.type === 'values' ? `${f.count} values` : `${f.mode} "${f.value}"`}
                </span>
              ))}
              <button onClick={() => { clearAllFilters(); setSortConfig({ key: null, direction: null }); }} style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Clear All</button>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '1700px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#1e3a5f', color: 'white' }}>
                    {columns.map(col => {
                      const isFiltered = (columnFilters[col.key] && columnFilters[col.key].length > 0) || filterText[col.key];
                      return (
                        <th key={col.key} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', whiteSpace: 'nowrap', minWidth: col.width, cursor: 'pointer', userSelect: 'none', backgroundColor: sortConfig.key === col.key ? '#162d4a' : undefined }} onClick={() => handleSort(col.key)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>{col.label}</span>
                            <span style={{ fontSize: '10px', opacity: sortConfig.key === col.key ? 1 : 0.5 }}>{getSortIcon(col.key)}</span>
                            <span onClick={(e) => openFilterAtPosition(e, col.key)} style={{ marginLeft: '2px', cursor: 'pointer', opacity: isFiltered ? 1 : 0.6 }}>
                              <FilterIcon isActive={isFiltered} color="white" />
                            </span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: '600', width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                        {welders.length === 0 ? 'No entries found. Click "Add Entry" to get started.' : 'No entries match the current filters.'}
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((w, idx) => {
                      const isFlagged = w.needs_renewal === true;
                      const isWarning = w.is_warning === true;
                      const rowBg = isFlagged ? '#f0e6e6' : isWarning ? '#fef9e6' : (idx % 2 === 0 ? '#fff' : '#f8fafc');
                      return (
                        <tr key={w.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: rowBg }}>
                          {editingId === w.id ? (
                            <>
                              {columns.filter(c => !['updated_by', 'updated_at', 'last_nde_operated_date', 'last_nde_job_number', 'last_nde_job_name'].includes(c.key)).map(col => (
                                <td key={col.key} style={{ padding: '4px 8px' }}>
                                  {renderEditableCell(editForm, setEditForm, col.key)}
                                </td>
                              ))}
                              <td style={{ padding: '4px 8px' }}>{w.last_nde_operated_date ? new Date(w.last_nde_operated_date).toLocaleDateString() : '-'}</td>
                              <td style={{ padding: '4px 8px' }}>{w.last_nde_job_number || '-'}</td>
                              <td style={{ padding: '4px 8px' }}>{w.last_nde_job_name || '-'}</td>
                              <td style={{ padding: '4px 8px' }}>{w.updated_by || '-'}</td>
                              <td style={{ padding: '4px 8px' }}>{w.updated_at ? new Date(w.updated_at).toLocaleString() : '-'}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  <button onClick={() => handleSave(editForm)} disabled={saving} style={btnStyle('#16a34a')}>Save</button>
                                  <button onClick={() => setEditingId(null)} style={btnStyle('#6b7280')}>Cancel</button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              {columns.map(col => (
                                <td key={col.key} style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: isFlagged && col.key === 'empl_name' ? '#dc2626' : isWarning && col.key === 'empl_name' ? '#d97706' : undefined, fontWeight: (isFlagged || isWarning) && col.key === 'empl_name' ? '600' : undefined }}>
                                  {isFlagged && col.key === 'empl_code' ? (
                                    <span><span style={{ marginRight: '4px', fontSize: '11px' }} title="Needs certificate renewal">🚩</span>{formatCellValue(w, col.key)}</span>
                                  ) : isWarning && col.key === 'empl_code' ? (
                                    <span><span style={{ marginRight: '4px', fontSize: '11px' }} title="Approaching expiry">⚠️</span>{formatCellValue(w, col.key)}</span>
                                  ) : formatCellValue(w, col.key)}
                                </td>
                              ))}
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  <button onClick={() => { setEditingId(w.id); setEditForm({ ...w }); }} style={btnStyle('#3b82f6')}>Edit</button>
                                  <button onClick={() => handleDelete(w.id)} style={btnStyle('#ef4444')}>Del</button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Filter Dropdown */}
          {openFilterColumn && (
            <FilterTemplateDropdown
              columnKey={openFilterColumn}
              columnLabel={columns.find(c => c.key === openFilterColumn)?.label}
              showTextFilters={true}
              isNumericColumn={numericColumnKeys.includes(openFilterColumn)}
              isDateColumn={dateColumnKeys.includes(openFilterColumn)}
              columnFilters={columnFilters}
              filterMode={filterMode}
              filterText={filterText}
              filterSearch={filterSearch}
              filterButtonPositions={filterButtonPositions}
              setOpenFilterColumn={setOpenFilterColumn}
              setFilterSearch={setFilterSearch}
              getUniqueValues={getUniqueValues}
              handleFilterToggle={handleFilterToggle}
              selectAllColumnFilter={selectAllColumnFilter}
              clearColumnFilter={clearColumnFilter}
              applyTextFilter={applyTextFilter}
            />
          )}
        </>
      )}

      {/* Help with Legend tab */}
      <EmployeeHelpShell
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Welder Continuity Log — Help"
        accent="#1e3a5f"
        guideId="welder-continuity-help"
        ListenButton={ListenButton}
        processContent={(
          <div style={{ fontSize: 13, lineHeight: 1.6, color: '#374151' }}>
            <p style={{ marginTop: 0 }}>
              Welders are picked from a <strong>searchable dropdown of all active employees</strong> on the single
              master list — free-typing a name is not allowed. That keeps every entry tied to a real 4-digit
              employee code.
            </p>
            <ul>
              <li><strong>Welder number sync:</strong> when you save an entry, the welder number is automatically
                written to that employee's row on the master list — now and in the future.</li>
              <li><strong>Excel import</strong> is validated the same way: rows must match an active employee.</li>
              <li><strong>Need Certificate</strong> flags welders with no activity in the last 6 months.</li>
              <li>If a welder is laid off (status changed on the Hiring/Layoff dashboard) they disappear from the
                dropdown, but their continuity history is preserved.</li>
            </ul>
          </div>
        )}
      />
    </div>
  );
};

/**
 * NeedCertificateAnalytics - Analytics view for welders needing certificate renewal
 */
const NeedCertificateAnalytics = ({ data, loading }) => {
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading analytics...</div>;
  }
  if (!data) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No analytics data available.</div>;
  }

  const { summary, flagged, expiringSoon } = data;

  const cardStyle = (color) => ({
    padding: '20px', borderRadius: '8px', border: '1px solid #e5e7eb',
    backgroundColor: '#fff', textAlign: 'center', borderTop: `3px solid ${color}`,
  });

  const renderTable = (rows, title, headerColor) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>{title} ({rows.length})</h3>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ backgroundColor: headerColor, color: 'white' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Welder #</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Empl Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Process</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Shop/Field</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Cert. Date</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Expiry Date</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Last NDE Operated</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Last NDE Job</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Assigned Job</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w, idx) => (
                <tr key={w.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ padding: '8px 12px', fontWeight: '600' }}>{w.welder_number || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.empl_name || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.process || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500',
                      backgroundColor: w.shop_field === 'shop' ? '#fef3c7' : '#dbeafe',
                      color: w.shop_field === 'shop' ? '#92400e' : '#1e40af',
                    }}>{w.shop_field || 'field'}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{w.certificate_date ? new Date(w.certificate_date).toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.expiry_date ? new Date(w.expiry_date).toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.last_nde_operated_date ? new Date(w.last_nde_operated_date).toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.last_nde_job_number ? `${w.last_nde_job_number}${w.last_nde_job_name ? ' - ' + w.last_nde_job_name : ''}` : '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{w.job_number_ref ? `${w.job_number_ref}${w.job_name ? ' - ' + w.job_name : ''}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={cardStyle('#3b82f6')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>{summary.total}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Total Entries</div>
        </div>
        <div style={cardStyle('#ef4444')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>{summary.needsRenewal}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Needs Renewal</div>
        </div>
        <div style={cardStyle('#f59e0b')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>{summary.expiringSoon}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Warning</div>
        </div>
        <div style={cardStyle('#10b981')}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#10b981' }}>{summary.valid}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Valid</div>
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '12px 16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#1e40af' }}>
        <strong>Flagging Rules:</strong> A welder needs certificate renewal (red) when their expiry date has passed AND last NDE operated is more than 6 months ago. Warning (amber) when expiry is within 1 month OR last NDE operated is more than 5 months ago.
      </div>

      {/* Flagged welders */}
      {renderTable(flagged, '🚩 Needs Certificate Renewal', '#dc2626')}

      {/* Warning */}
      {renderTable(expiringSoon, '⚠️ Warning — Approaching Expiry', '#d97706')}

      {/* Valid */}
      {renderTable(data.valid, '✓ Valid', '#16a34a')}
    </div>
  );
};

/**
 * JobSearchInput - Searchable dropdown for job number/name
 * Typing triggers search, shows both columns, ESC closes dropdown.
 */
const JobSearchInput = ({ form, setForm, field, inputStyle, instance, accounts }) => {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const ref = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    setForm(p => ({ ...p, [field]: val }));

    if (searchTimeout) clearTimeout(searchTimeout);
    if (val.length >= 1) {
      setSearchTimeout(setTimeout(async () => {
        try {
          const response = await authenticatedApiCall(instance, accounts, 'GET', `/welder-log/search-projects?q=${encodeURIComponent(val)}`);
          if (response.data.success) {
            setResults(response.data.data || []);
            setOpen(true);
          }
        } catch (err) {
          console.error('Search error:', err);
        }
      }, 300));
    } else {
      setOpen(false);
      setResults([]);
    }
  };

  const handleSelect = (project) => {
    setForm(p => ({ ...p, job_number_ref: project.job_number, job_name: project.name }));
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        value={form[field] || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        style={inputStyle}
        placeholder={field === 'job_number_ref' ? 'Search job #...' : 'Search job name...'}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflowY: 'auto',
        }}>
          {results.map((proj, i) => (
            <div
              key={i}
              onClick={() => handleSelect(proj)}
              style={{
                padding: '8px 10px', cursor: 'pointer', fontSize: '12px',
                display: 'flex', gap: '12px', borderBottom: '1px solid #f3f4f6',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
            >
              <span style={{ fontWeight: '600', minWidth: '80px' }}>{proj.job_number}</span>
              <span style={{ color: '#6b7280' }}>{proj.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WelderContinuityLog;
